import { useCallback, useEffect, useState, useMemo } from "react";
import { getConfirmedOrders } from "../../Services/orderService";
import { Form, Card } from "react-bootstrap";
import { MdDashboard } from "react-icons/md";
import { GrDeliver } from "react-icons/gr";
import { GiReturnArrow } from "react-icons/gi";
import { CiAlarmOn } from "react-icons/ci";

import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    Legend,
} from "recharts";
import { useSelector } from "react-redux";
import KPITile from "../../Components/KPITiles";


function useAnalytics(orders) {
    return useMemo(() => {
        const total = orders.length;
        const delivered = orders.filter(o => o.status === "delivered");
        const pending = orders.filter(o => o.status === "pending");
        const returned = orders.filter(o => o.status === "returned");
        const cancelled = orders.filter(o => o.status === "cancelled");
        const revenue = delivered.reduce((s, o) => s + parseInt(o.amount), 0);
        const totalRevenue = orders.reduce((s, o) => s + parseInt(o.amount), 0);
        const avgOrderValue = totalRevenue / total;
        const deliveryRate = ((delivered.length / total) * 100).toFixed(1);
        const returnRate = ((returned.length / total) * 100).toFixed(1);
        const uniqueCustomers = new Set(orders.map(o => o.customerName.toLowerCase())).size;

        // By city
        const cityMap = {};
        orders.forEach(o => {
            const c = o.city || "Unknown";
            cityMap[c] = (cityMap[c] || 0) + 1;
        });
        const byCity = Object.entries(cityMap).sort((a, b) => b[1] - a[1]);

        // By status
        const byStatus = [
            { name: "Delivered", value: delivered.length, color: "#10b981" },
            { name: "Pending", value: pending.length, color: "#f59e0b" },
            { name: "Returned", value: returned.length, color: "#ef4444" },
            { name: "Cancelled", value: cancelled.length, color: "#6b7280" },
        ];

        // By product
        const prodMap = {};
        orders.forEach(o => { prodMap[o.articleName] = (prodMap[o.articleName] || 0) + 1; });
        const byProduct = Object.entries(prodMap).sort((a, b) => b[1] - a[1]);

        // By source
        const srcMap = {};
        orders.forEach(o => { const s = o.source || "phone"; srcMap[s] = (srcMap[s] || 0) + 1; });
        const bySource = Object.entries(srcMap);

        // Daily trend (by date)
        const dayMap = {};
        orders.forEach(o => {
            const d = o.createdAt.split("T")[0];
            if (!dayMap[d]) dayMap[d] = { date: d, orders: 0, revenue: 0, delivered: 0, returned: 0 };
            dayMap[d].orders++;
            dayMap[d].revenue += parseInt(o.amount);
            if (o.status === "delivered") dayMap[d].delivered++;
            if (o.status === "returned") dayMap[d].returned++;
        });
        const dailyTrend = Object.values(dayMap).sort((a, b) => a.date.localeCompare(b.date));

        // By agent
        const agentMap = {};
        orders.forEach(o => {
            const ag = o.bookingAgent === "-" ? "Direct" : o.bookingAgent;
            if (!agentMap[ag]) agentMap[ag] = { name: ag, orders: 0, revenue: 0, delivered: 0 };
            agentMap[ag].orders++;
            agentMap[ag].revenue += parseInt(o.amount);
            if (o.status === "delivered") agentMap[ag].delivered++;
        });
        const byAgent = Object.values(agentMap).sort((a, b) => b.orders - a.orders);

        // Top customers by amount
        const custMap = {};
        orders.forEach(o => {
            const k = o.customerName;
            if (!custMap[k]) custMap[k] = { name: k, orders: 0, amount: 0, city: o.city };
            custMap[k].orders++;
            custMap[k].amount += parseInt(o.amount);
        });
        const topCustomers = Object.values(custMap).sort((a, b) => b.amount - a.amount).slice(0, 8);

        // Revenue by city
        const cityRevMap = {};
        orders.forEach(o => {
            const c = o.city || "Unknown";
            cityRevMap[c] = (cityRevMap[c] || 0) + parseInt(o.amount);
        });
        const revenueByCity = Object.entries(cityRevMap).sort((a, b) => b[1] - a[1]).slice(0, 8);

        // Weekly data (group by week)
        const weekMap = {};
        orders.forEach(o => {
            const d = new Date(o.createdAt);
            const week = `W${Math.ceil(d.getDate() / 7)} ${d.toLocaleString("default", { month: "short" })}`;
            if (!weekMap[week]) weekMap[week] = { week, orders: 0, revenue: 0 };
            weekMap[week].orders++;
            weekMap[week].revenue += parseInt(o.amount);
        });
        const weeklyTrend = Object.values(weekMap);

        // Pending > 7 days alerts
        const now = new Date();
        const stalePending = pending.filter(o => (now - new Date(o.createdAt)) / 86400000 > 7);

        // Pending with no VPP
        const noVpp = pending.filter(o => !o.VppNumber);

        return {
            total, delivered: delivered.length, pending: pending.length,
            returned: returned.length, cancelled: cancelled.length,
            revenue, totalRevenue, avgOrderValue, deliveryRate, returnRate,
            uniqueCustomers, byCity, byStatus, byProduct, bySource,
            dailyTrend, byAgent, topCustomers, revenueByCity, weeklyTrend,
            stalePending, noVpp,
            pendingOrders: pending,
        };
    }, [orders]);
}

// Pending > 7 days alerts
const now = new Date();
// const stalePending = pending.filter(o => (now - new Date(o.createdAt)) / 86400000 > 7);



const DashboardV2 = () => {
    const [orders, setOrders] = useState([]);
    const [page, setPage] = useState(1);
    const [filters, setFilters] = useState({
        status: "all",
        from: "",
        to: "",
        trackingId: "",
    });
    const [loading, setLoading] = useState(false);
    const analytics = useAnalytics(orders);


    const companyPin = useSelector(state => state.auth.companyPin);
    const base64Pin = btoa(companyPin);


    // ✅ Fetch Orders
    const fetchOrders = useCallback(async () => {
        try {
            setLoading(true);

            const res = await getConfirmedOrders({
                page,
                status: filters.status !== "all" ? filters.status : undefined,
                company_pin: base64Pin

            });

            if (res?.status === "success") {
                setOrders(res.data || []);
            } else {
                setOrders([]);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [page, filters]);

    useEffect(() => {
        fetchOrders();
    }, [fetchOrders]);



    return (
        <>

            {/* PAGE TITLE */}
            <div className="section mb-3" style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between" }}>
                <div>
                    <h1 style={{ fontFamily: "'Syne',sans-serif", fontSize: 26, fontWeight: 800, marginBottom: 4 }}>
                        Analytics Overview
                    </h1>
                    <p style={{ fontSize: 14, color: "var(--text-muted)" }}>
                        Complete intelligence dashboard · {orders.length} records · Last updated {new Date(Math.max(...orders.map(o => new Date(o.updatedAt)))).toLocaleDateString()}
                    </p>
                </div>
                <div className="top-right">
                    <div className="now-badge">
                        <span className="dot-live" />
                        {now.toLocaleTimeString()} — {now.toLocaleDateString("en-PK", { weekday: "short", day: "numeric", month: "short", year: "numeric" })}
                    </div>
                </div>
            </div>

            <section>

                <div className="row g-3">

                    <div className="col-lg-3">
                        <KPITile icon="📦" label="Total Orders" value={analytics.total} color="#6366f1" sub="All time records" trend={8} delay={0} />
                    </div>
                    <div className="col-lg-3">
                        <KPITile icon="✅" label="Delivered" value={analytics.delivered} color="#10b981" sub={`PKR ${analytics.revenue.toLocaleString()} collected`} trend={12} delay={80} />
                    </div>
                    <div className="col-lg-3">
                        <KPITile icon="⏳" label="Pending" value={analytics.pending} color="#f59e0b" sub={`${analytics.stalePending.length} overdue 7d+`} trend={-5} delay={160} />
                    </div>
                    <div className="col-lg-3">
                        <KPITile icon="↩" label="Returned" value={analytics.returned} color="#ef4444" sub={`${analytics.returnRate}% return rate`} trend={-3} delay={240} />
                    </div>



                    <div className="col-lg-3">
                        <KPITile icon="💰" label="Total Revenue" value={analytics.totalRevenue} prefix="PKR " color="#10b981" sub="Incl. pending COD" trend={6} decimals={0} delay={0} />

                    </div>
                    <div className="col-lg-3">
                        <KPITile icon="👤" label="Unique Customers" value={analytics.uniqueCustomers} color="#8b5cf6" sub="Distinct buyers" delay={80} />

                    </div>
                    <div className="col-lg-3">
                        <KPITile icon="🛍" label="Avg Order Value" value={analytics.avgOrderValue} prefix="PKR " color="#f59e0b" sub="Per transaction" decimals={0} delay={160} />

                    </div>
                    <div className="col-lg-3">
                        <KPITile icon="🏙️" label="Cities Served" value={analytics.byCity.length} color="#06b6d4" sub={`Top: ${analytics.byCity[0]?.[0]}`} delay={240} />

                    </div>
                </div >
            </section>

        </>
    );
};

export default DashboardV2;




